/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useMemo, createContext, useContext } from 'react';
import type { NavigationCollectionsRemote } from '@jira/polaris__component-navigation-collections-store/src/TypesRemoteNavigationCollection.tsx';
import type { NavigationViewRemote } from '@jira/polaris__component-navigation-store/src/TypesRemoteViewRemover.tsx';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { createNavigationCollectionsRemote } from './collection/index.tsx';
import { createNavigationViewRemote } from './view/index.tsx';

const createNavigationRemoteProvider = <T,>(
	createRemote: (createAnalyticsEvent: CreateUIAnalyticsEvent) => T,
) => {
	const NavigationRemoteContext = createContext<T | undefined>(undefined);

	const NavigationRemoteProvider = ({
		children,
	}: {
		children: React.ReactNode;
	}): React.JSX.Element => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const remote = useMemo(() => createRemote(createAnalyticsEvent), [createAnalyticsEvent]);

		return (
			<NavigationRemoteContext.Provider value={remote}>{children}</NavigationRemoteContext.Provider>
		);
	};

	const useNavigationRemote = (): T | undefined => useContext(NavigationRemoteContext);

	return {
		NavigationRemoteProvider,
		useNavigationRemote,
	};
};

const collectionsResult = createNavigationRemoteProvider(createNavigationCollectionsRemote);
const NavigationCollectionsRemoteProvider: ({
	children,
}: {
	children: React.ReactNode;
}) => React.JSX.Element = collectionsResult.NavigationRemoteProvider;
const useNavigationCollectionsRemote: () => NavigationCollectionsRemote | undefined =
	collectionsResult.useNavigationRemote;

const viewResult = createNavigationRemoteProvider(createNavigationViewRemote);
const NavigationViewRemoteProvider: ({
	children,
}: {
	children: React.ReactNode;
}) => React.JSX.Element = viewResult.NavigationRemoteProvider;
const useNavigationViewRemote: () => NavigationViewRemote | undefined =
	viewResult.useNavigationRemote;

export {
	NavigationCollectionsRemoteProvider,
	useNavigationCollectionsRemote,
	NavigationViewRemoteProvider,
	useNavigationViewRemote,
};
